import React, { useEffect, useRef, useState } from 'react'
import Layout from '../../components/Layout/Layout'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { convert } from '../../utils/convertDate';
import DatePicker from 'react-date-picker'
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { toast } from 'react-toastify';
import WakanowServices from '../../services/wakanowServices';
import BackButton from '../../components/BackButton';

function parseTravelPeriod(str) {
    if (!str) return { start: null, end: null };
    const parts = str.split('to');
    if (parts.length === 2) {
        const start = new Date(parts[0].trim());
        const end = new Date(parts[1].trim());
        return {
            start: isNaN(start.getTime()) ? null : start,
            end: isNaN(end.getTime()) ? null : end
        };
    }
    return { start: null, end: null };
}

function formatTravelPeriod(start, end) {
    if (!start || !end) return "";
    const opts = { year: 'numeric', month: 'long', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', opts)} to ${end.toLocaleDateString('en-US', opts)}`;
}

const CreateHolidayPackage = () => {
    const location = useLocation();
    const { slug } = useParams();
    const queryParams = new URLSearchParams(location.search);
    const packageIdFromQuery = queryParams.keys().next().value;

    const isCreateMode = slug === "create";
    const isViewMode = slug === "view";
    const isEditMode = slug === "edit";

    const [packageName, setPackageName] = useState("");
    const [destination, setDestination] = useState("");
    const [travelStartDate, setTravelStartDate] = useState(null);
    const [travelEndDate, setTravelEndDate] = useState(null);
    const [description, setDescription] = useState("");
    const [inclusions, setInclusions] = useState("");
    const [isFlightIncluded, setIsFlightIncluded] = useState(false);
    const [startingPrice, setStartingPrice] = useState("");
    const [packageImages, setPackageImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [existingImageUrls, setExistingImageUrls] = useState([]);
    const [removedImageUrls, setRemovedImageUrls] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isListLoading, setIsListLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [packageData, setPackageData] = useState(null);

    const fileInputRef = useRef(null);

    const navigate = useNavigate();

    useEffect(() => {
        const GetPackage = async () => {
            setIsListLoading(true)
            try {
                const response = await WakanowServices.getHolidayPackageById(packageIdFromQuery);
                if (response) {
                    const data = response.data;
                    setPackageData(data);
                    setPackageName(data.packageName);
                    setDestination(data.destination);
                    const parsed = parseTravelPeriod(data.travelPeriod);
                    setTravelStartDate(parsed.start);
                    setTravelEndDate(parsed.end);
                    setDescription(data.description);
                    setInclusions(data.inclusions);
                    setIsFlightIncluded(data.isFlightIncluded);
                    setStartingPrice(data.startingPrice ?? "");
                    setExistingImageUrls(data.imageUrls || []);
                    setIsListLoading(false)
                }
            } catch (error) {
                setIsListLoading(false)
            } finally {
                setIsListLoading(false)
            }
        }
        if (!isCreateMode) {
            GetPackage();
        }
    }, [slug, packageIdFromQuery])

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const newImages = [...packageImages, ...files];
            const newPreviews = [
                ...imagePreviews,
                ...files.map((f) => URL.createObjectURL(f)),
            ];
            setPackageImages(newImages);
            setImagePreviews(newPreviews);
        }
    };

    const handleRemoveNewImage = (index) => {
        const newImages = packageImages.filter((_, i) => i !== index);
        const newPreviews = imagePreviews.filter((_, i) => i !== index);
        URL.revokeObjectURL(imagePreviews[index]);
        setPackageImages(newImages);
        setImagePreviews(newPreviews);
    };

    const handleRemoveExistingImage = (url) => {
        setRemovedImageUrls((prev) => [...prev, url]);
        setExistingImageUrls((prev) => prev.filter((u) => u !== url));
    };

    const handleSaveInternal = async () => {
        setIsLoading(true)
        try {
            const formData = new FormData();
            formData.append('PackageName', packageName);
            formData.append('Destination', destination);
            formData.append('TravelPeriod', formatTravelPeriod(travelStartDate, travelEndDate));
            formData.append('Description', description);
            formData.append('Inclusions', inclusions);
            formData.append('IsFlightIncluded', isFlightIncluded);

            if (isEditMode) {
                formData.append('HolidayPackageId', packageIdFromQuery);
                formData.append('StartingPrice', startingPrice);
                packageImages.forEach((file) => {
                    formData.append('PackageImages', file);
                });
                if (existingImageUrls.length > 0) {
                    formData.append('ExistingImageUrls', JSON.stringify(existingImageUrls));
                }
                if (removedImageUrls.length > 0) {
                    formData.append('RemovedImageUrls', JSON.stringify(removedImageUrls));
                }
                const response = await WakanowServices.updateHolidayPackage(formData);
                if (response) {
                    toast.success(response.message || "Package updated successfully");
                    setIsLoading(false)
                    setTimeout(() => {
                        navigate("/wakanow/holiday-packages")
                    }, 2000);
                } else {
                    setIsLoading(false)
                }
            } else {
                formData.append('StartingPrice', startingPrice);
                packageImages.forEach((file) => {
                    formData.append('PackageImages', file);
                });
                const response = await WakanowServices.createHolidayPackage(formData);
                if (response) {
                    toast.success(response.message)
                    setTimeout(() => {
                        setIsLoading(false)
                        navigate("/wakanow/holiday-packages")
                    }, 2000);
                } else {
                    setIsLoading(false)
                }
            }
        } catch (error) {
            setIsLoading(false)
        }
    };

    const confirmChanges = async () => {
        if (!packageName || !destination || !travelStartDate || !travelEndDate || !description || !inclusions) {
            toast.error("Please fill in all required fields.");
            return;
        }
        if (startingPrice === "" || isNaN(Number(startingPrice)) || Number(startingPrice) < 0) {
            toast.error("Please enter a valid starting price.");
            return;
        }
        if (isCreateMode && packageImages.length === 0) {
            toast.error("Please select at least one package image.");
            return;
        }
        setShowPreview(true);
    };

    const handleConfirmSave = async () => {
        setShowPreview(false);
        await handleSaveInternal();
    };

    const handleCancelPreview = () => {
        setShowPreview(false);
        setIsLoading(false);
    };

    if (isListLoading) {
        return (
            <Layout title="Holiday Package" pageTag={"wakanow"} current={'/wakanow/holiday-packages'}>
                <div className="min-h-full">
                    <div className="flex flex-1 flex-col">
                        <main className="flex-1">
                            <div className="py-6">
                                <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
                                    <div className="bg-white shadow sm:rounded-lg">
                                        <div className="px-4 py-5 sm:p-6">
                                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                                {Array.from({ length: 6 }).map((_, index) => (
                                                    <div key={index}>
                                                        <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2"></div>
                                                        <div className="h-10 bg-gray-200 rounded animate-pulse w-full"></div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </Layout>
        )
    }

    return (
        <Layout
            title={isViewMode ? "View Holiday Package" : (isEditMode ? "Edit Holiday Package" : "Create Holiday Package")}
            pageTag={"wakanow"}
            current={'/wakanow/holiday-packages'}
        >
            <React.Fragment>
                <div className="min-h-full">
                    <div className="flex flex-1 flex-col">
                        <BackButton />
                        {packageData && !isCreateMode && (
                            <div className="flex items-center justify-end w-full pr-10 font-bold">
                                <span className={packageData.status?.toLowerCase() === "approved" ? "text-green-500" : "text-gray-500"}>
                                    {packageData.status}
                                </span>
                            </div>
                        )}
                        <main className="flex-1">
                            <div className="py-6">
                                <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
                                    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                                        <div className="px-4 py-5 sm:p-6">
                                            {isViewMode ? (
                                                <div>
                                                    {(existingImageUrls.length > 0) && (
                                                        <div className="relative mb-6 -mx-6 -mt-6 group">
                                                            <img
                                                                src={existingImageUrls[currentImageIndex]}
                                                                alt={packageName}
                                                                className="w-full h-64 object-cover"
                                                            />
                                                            {existingImageUrls.length > 1 && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setCurrentImageIndex(
                                                                                (prev) =>
                                                                                    prev === 0
                                                                                        ? existingImageUrls.length - 1
                                                                                        : prev - 1
                                                                            )
                                                                        }
                                                                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    >
                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setCurrentImageIndex(
                                                                                (prev) =>
                                                                                    prev === existingImageUrls.length - 1
                                                                                        ? 0
                                                                                        : prev + 1
                                                                            )
                                                                        }
                                                                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    >
                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                                    </button>
                                                                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                                                                        {existingImageUrls.map((_, idx) => (
                                                                            <button
                                                                                key={idx}
                                                                                type="button"
                                                                                onClick={() => setCurrentImageIndex(idx)}
                                                                                className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex
                                                                                    ? 'bg-white w-3'
                                                                                    : 'bg-white/50 hover:bg-white/80'
                                                                                    }`}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Package Name</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">{packageName}</dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Destination</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">{destination}</dd>
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <dt className="text-sm font-medium text-gray-500">Travel Period</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">
                                                                {travelStartDate && travelEndDate
                                                                    ? formatTravelPeriod(travelStartDate, travelEndDate)
                                                                    : packageData?.travelPeriod || 'N/A'}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Flight Included</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">
                                                                {isFlightIncluded ? 'Yes' : 'No'}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Starting Price</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">
                                                                {startingPrice !== "" && startingPrice != null ? `₦${parseFloat(startingPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Status</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">{packageData?.status || 'N/A'}</dd>
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <dt className="text-sm font-medium text-gray-500">Description</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">{description}</dd>
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <dt className="text-sm font-medium text-gray-500">Inclusions</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">{inclusions}</dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Created By</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">{packageData?.createdBy || 'N/A'}</dd>
                                                        </div>
                                                        <div>
                                                            <dt className="text-sm font-medium text-gray-500">Date Created</dt>
                                                            <dd className="mt-1 text-sm text-gray-900">
                                                                {packageData?.dateCreated ? convert(packageData.dateCreated) : 'N/A'}
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Package Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="mt-1">
                                                            <input
                                                                type="text"
                                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                                value={packageName}
                                                                onChange={(e) => setPackageName(e.target.value)}
                                                                placeholder="e.g. Summer in Bali"
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Destination <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="mt-1">
                                                            <input
                                                                type="text"
                                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                                value={destination}
                                                                onChange={(e) => setDestination(e.target.value)}
                                                                placeholder="e.g. Bali, Indonesia"
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="sm:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Travel Period <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">Start Date</p>
                                                                <DatePicker
                                                                    onChange={setTravelStartDate}
                                                                    value={travelStartDate}
                                                                    className="w-full"
                                                                />
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">End Date</p>
                                                                <DatePicker
                                                                    onChange={setTravelEndDate}
                                                                    value={travelEndDate}
                                                                    className="w-full"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center pt-6">
                                                        <label className="relative inline-flex items-center cursor-pointer mr-3">
                                                            <input
                                                                type="checkbox"
                                                                className="sr-only peer"
                                                                checked={isFlightIncluded}
                                                                onChange={(e) => setIsFlightIncluded(e.target.checked)}
                                                            />
                                                            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                                        </label>
                                                        <span className="text-sm font-medium text-gray-700">
                                                            Flight Included
                                                        </span>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Starting Price <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="mt-1 relative rounded-md shadow-sm">
                                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                <span className="text-gray-500 sm:text-sm">₦</span>
                                                            </div>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-8 sm:text-sm border-gray-300 rounded-md"
                                                                value={startingPrice}
                                                                onChange={(e) => setStartingPrice(e.target.value)}
                                                                placeholder="0.00"
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="sm:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Description <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="mt-1">
                                                            <textarea
                                                                rows={3}
                                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                                value={description}
                                                                onChange={(e) => setDescription(e.target.value)}
                                                                placeholder="Describe the holiday package..."
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="sm:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700">
                                                            Inclusions <span className="text-red-500">*</span>
                                                        </label>
                                                        <div className="mt-1">
                                                            <textarea
                                                                rows={3}
                                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                                value={inclusions}
                                                                onChange={(e) => setInclusions(e.target.value)}
                                                                placeholder="What's included (e.g. Hotel, Meals, Tour Guide)"
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="sm:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Package Images {isCreateMode && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <div
                                                            className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-gray-300 hover:border-indigo-400 transition-colors cursor-pointer"
                                                            onClick={() => fileInputRef.current?.click()}
                                                        >
                                                            <div className="space-y-1 text-center w-full">
                                                                {(imagePreviews.length > 0 || existingImageUrls.length > 0) ? (
                                                                    <div className="flex flex-wrap gap-3 justify-center">
                                                                        {existingImageUrls.map((url, idx) => (
                                                                            <div key={`existing-${idx}`} className="relative inline-block">
                                                                                <img
                                                                                    src={url}
                                                                                    alt={`Package image ${idx + 1}`}
                                                                                    className="h-28 w-28 object-cover rounded-lg shadow-md"
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => { e.stopPropagation(); handleRemoveExistingImage(url); }}
                                                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow hover:bg-red-600"
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                        {imagePreviews.map((preview, idx) => (
                                                                            <div key={`new-${idx}`} className="relative inline-block">
                                                                                <img
                                                                                    src={preview}
                                                                                    alt={`New image ${idx + 1}`}
                                                                                    className="h-28 w-28 object-cover rounded-lg shadow-md"
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => { e.stopPropagation(); handleRemoveNewImage(idx); }}
                                                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow hover:bg-red-600"
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                        <div
                                                                            className="h-28 w-28 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-400 transition-colors"
                                                                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                                                        >
                                                                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                                                                        <svg
                                                                            className="mx-auto h-12 w-12 text-gray-400"
                                                                            stroke="currentColor"
                                                                            fill="none"
                                                                            viewBox="0 0 48 48"
                                                                            aria-hidden="true"
                                                                        >
                                                                            <path
                                                                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                                                strokeWidth={2}
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            />
                                                                        </svg>
                                                                        <div className="flex text-sm text-gray-600">
                                                                            <label className="relative cursor-pointer rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none">
                                                                                <span>Upload images</span>
                                                                            </label>
                                                                            <p className="pl-1">or drag and drop</p>
                                                                        </div>
                                                                        <p className="text-xs text-gray-500">PNG, JPG up to 5MB each</p>
                                                                    </div>
                                                                )}
                                                                <input
                                                                    ref={fileInputRef}
                                                                    type="file"
                                                                    accept="image/*"
                                                                    multiple
                                                                    className="hidden"
                                                                    onChange={handleImageChange}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {!isViewMode && (
                                                <div className="mt-8 flex justify-end border-t border-gray-200 pt-6">
                                                    <button
                                                        type="button"
                                                        onClick={confirmChanges}
                                                        className="inline-flex justify-center rounded-md border border-transparent bg-gray-800 py-2.5 px-6 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                                                    >
                                                        {isLoading ? (
                                                            <span className="flex items-center">
                                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                Saving...
                                                            </span>
                                                        ) : (
                                                            isEditMode ? "Update Package" : "Create Package"
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </React.Fragment>

            {isLoading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75">
                    <div className="bg-white rounded-xl shadow-xl p-8 flex flex-col items-center">
                        <svg className="animate-spin h-10 w-10 text-gray-800 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-700 font-medium">
                            {isEditMode ? "Updating holiday package..." : "Creating holiday package..."}
                        </p>
                    </div>
                </div>
            )}

            {showPreview && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCancelPreview} />
                        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full overflow-hidden transform transition-all sm:my-8">
                            <div className="px-6 py-5 sm:p-6">
                                <div className="text-center sm:text-left">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                        {isEditMode ? "Preview Changes" : "Preview Holiday Package"}
                                    </h3>
                                    {(imagePreviews.length > 0 || existingImageUrls.length > 0) && (
                                        <div className="-mx-6 -mt-5 mb-5 flex flex-wrap gap-2 justify-center px-6">
                                            {existingImageUrls.map((url, idx) => (
                                                <img
                                                    key={`existing-${idx}`}
                                                    src={url}
                                                    alt={`${packageName} ${idx + 1}`}
                                                    className="h-28 w-28 object-cover rounded-lg shadow-md"
                                                />
                                            ))}
                                            {imagePreviews.map((preview, idx) => (
                                                <img
                                                    key={`preview-${idx}`}
                                                    src={preview}
                                                    alt={`New image ${idx + 1}`}
                                                    className="h-28 w-28 object-cover rounded-lg shadow-md"
                                                />
                                            ))}
                                        </div>
                                    )}
                                    <dl className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2 text-left">
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Package Name</dt>
                                            <dd className="mt-1 text-sm font-medium text-gray-900">{packageName}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Destination</dt>
                                            <dd className="mt-1 text-sm font-medium text-gray-900">{destination}</dd>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Travel Period</dt>
                                            <dd className="mt-1 text-sm font-medium text-gray-900">
                                                {travelStartDate && travelEndDate
                                                    ? formatTravelPeriod(travelStartDate, travelEndDate)
                                                    : 'Not set'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Flight Included</dt>
                                            <dd className="mt-1 text-sm font-medium text-gray-900">{isFlightIncluded ? 'Yes' : 'No'}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Starting Price</dt>
                                            <dd className="mt-1 text-sm font-medium text-gray-900">
                                                {startingPrice !== "" && startingPrice != null ? `₦${parseFloat(startingPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                                            </dd>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</dt>
                                            <dd className="mt-1 text-sm text-gray-700">{description}</dd>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Inclusions</dt>
                                            <dd className="mt-1 text-sm text-gray-700">{inclusions}</dd>
                                        </div>
                                    </dl>
                                </div>
                                <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                                    <button
                                        type="button"
                                        onClick={handleConfirmSave}
                                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto sm:ml-3"
                                    >
                                        {isEditMode ? "Save Changes" : "Create Package"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelPreview}
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto"
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    )
}

export default CreateHolidayPackage
